import ContentManager from '@tribal-worldwide-london/fa-content';
import deepmerge from 'deepmerge';
import { type GlobalStoreStateConfiguration } from '@/stores/globalStore';
import { reduceDataByKey } from '../utils/object';
import { handleAllErrors } from '../utils/response';
import VwgProductService from '@tribal-worldwide-london/fa-product-service-vwg/dist/interfaces/VwgProductService';

export type ProductLoaderFunctionArgs = {
  params: {
    configurations: GlobalStoreStateConfiguration[];
  };
};

export type ProductLoaderServiceArgs = {
  productService: VwgProductService;
};

const getTechnologyItemRequests = (
  productService: VwgProductService,
  configurations: GlobalStoreStateConfiguration[],
  getIds: (configuration: GlobalStoreStateConfiguration) => number[]
) => {
  const requests: Promise<any>[] = [];
  const fullUrls: string[] = [];

  configurations.forEach((configuration) => {
    const ids = getIds(configuration);

    if (!!ids?.length) {
      fullUrls.push(configuration.fullUrl);
      requests.push(productService.getTechnologyItemsById(ids));
    }
  });

  return {
    fullUrls,
    requests,
  };
};

const getSpecOptionsRequests = (
  productService: VwgProductService,
  configurations: GlobalStoreStateConfiguration[],
) => {
  const requests: Promise<any>[] = [];
  const fullUrls: string[] = [];

  configurations.forEach((configuration) => {
    if (configuration.pdbData?.derivative?.id) {
      fullUrls.push(configuration.fullUrl);
      requests.push(productService.getSpecItemsForDerivative(configuration.pdbData?.derivative?.id));
    }
  });

  return { 
    fullUrls,
    requests,
  };
};

const productLoader = async (
  { productService }: ProductLoaderServiceArgs,
  { params }: ProductLoaderFunctionArgs,
  contentManager: Pick<ContentManager, 'get'>
) => {
  const { configurations } = params;

  const liked = getTechnologyItemRequests(
    productService,
    configurations,
    (configuration) => configuration.pdbData?.technologyItems?.liked || []
  );

  const extras = getTechnologyItemRequests(
    productService,
    configurations,
    (configuration) => configuration.pdbData?.technologyItems?.extras || []
  );

  const standard = getTechnologyItemRequests(
    productService,
    configurations,
    (configuration) => configuration.pdbData?.technologyItems?.standard || []
  );

  const specOptions = getSpecOptionsRequests(
    productService,
    configurations,
  );

  const productResponses = await Promise.all([
    Promise.all(liked.requests),
    Promise.all(extras.requests),
    Promise.all(standard.requests),
    Promise.all(specOptions.requests),
  ]);

const allResponses = productResponses.flat();
  handleAllErrors(allResponses, contentManager, true);

  const likedDataByUrl = reduceDataByKey(
    liked.fullUrls,
    (key: string, i: number) => ({
      [key]: {
        technologyItems: { liked: productResponses[0][i].data.technologyItems },
      },
    })
  );

  const extrasDataByUrl = reduceDataByKey(
    extras.fullUrls,
    (key: string, i: number) => ({
      [key]: {
        technologyItems: {
          extras: productResponses[1][i].data.technologyItems,
        },
      },
    })
  );

  const standardDataByUrl = reduceDataByKey(
    standard.fullUrls,
    (key: string, i: number) => ({
      [key]: {
        technologyItems: {
          standard: productResponses[2][i].data.technologyItems,
        },
      },
    })
  );

  const specOptionsDataByUrl = reduceDataByKey(
    specOptions.fullUrls,
    (key: string, i: number) => ({
      [key]: {
        options: productResponses[3][i].data
      },
    })
  );

  return Promise.resolve(
    deepmerge.all([likedDataByUrl, extrasDataByUrl, standardDataByUrl, specOptionsDataByUrl])
  );
};

export default productLoader;
